/**
 * useViewerAccess: who is looking, for the surfaces that show contact and rights-holder data
 * (owner decision, 2026-10-13: fully set-up accounts only). See `src/lib/account-setup.ts` for
 * the rule and the five answers.
 *
 * Shaped after `useAccountStatus`, with two differences that matter for what a page renders:
 *   - The answer belongs to one user id. When the id changes (sign in, sign out, another account)
 *     the hook says "loading" until that account's own profile is read; it never hands the next
 *     account the previous one's answer.
 *   - A profile that can't be read answers "incomplete", never "set-up" (fail closed). One retry
 *     first, as the other profile gates do, so a network blip doesn't tell a set-up account to
 *     finish setting up.
 *
 * A profile already cached and fresh answers on the first render, so a set-up account sees its
 * content without a loader in between.
 *
 * The profile can change under the same account: onboarding finishes, an approval lands, an invite
 * is accepted. Each of those fires the app's `profileUpdated` event, and the hook reads the profile
 * again then; the answer on screen stays until the new one lands. `PlayerProvider` lives for the
 * whole session, so without this an account that finished setting up would keep hearing what an
 * unfinished one hears until a reload.
 */
import { useEffect, useRef, useState } from "react";
import type { User } from "@supabase/supabase-js";

import { viewerAccessFor, type AccountSetupFields, type ViewerAccess } from "@/lib/account-setup";
import { UserCacheService } from "@/lib/user-cache";
import { useAuth } from "@/providers/AuthProvider";

export type ViewerAccessState = "loading" | ViewerAccess;

export interface ViewerAccessResult {
  access: ViewerAccessState;
  user: User | null;
  /** The profile the answer was made from: null while loading, signed out, or when it couldn't be read. */
  profile: AccountSetupFields | null;
}

/** An answer, and the account it was made for. */
interface Answer {
  userId: string;
  access: ViewerAccess;
  profile: AccountSetupFields | null;
  /** The profile couldn't be read: "incomplete" until a later read succeeds. */
  failed?: boolean;
}

const PROFILE_FETCH_ATTEMPTS = 2;
/** After a failed read, read again this long after each failure in a row (the last one repeats). */
export const PROFILE_REREAD_DELAYS_MS = [10_000, 30_000, 90_000, 300_000];

function cachedAnswer(userId: string | null): Answer | null {
  if (!userId) return null;
  const profile = UserCacheService.getCachedProfileSync(userId);
  return profile ? { userId, access: viewerAccessFor(true, profile), profile } : null;
}

export function useViewerAccess(): ViewerAccessResult {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [answer, setAnswer] = useState<Answer | null>(() => cachedAnswer(userId));
  const [reloadKey, setReloadKey] = useState(0);
  /** Failed reads in a row, and whose: how long the next re-read waits. */
  const failures = useRef<{ userId: string | null; count: number }>({ userId: null, count: 0 });

  useEffect(() => {
    if (!userId) return;
    const handler = () => {
      UserCacheService.clearCache(userId);
      setReloadKey((key) => key + 1);
    };
    window.addEventListener("profileUpdated", handler);
    return () => window.removeEventListener("profileUpdated", handler);
  }, [userId]);

  useEffect(() => {
    if (authLoading || !userId) return;
    let cancelled = false;

    (async () => {
      for (let attempt = 0; attempt < PROFILE_FETCH_ATTEMPTS; attempt++) {
        try {
          const profile = await UserCacheService.getProfile(userId);
          if (cancelled) return;
          failures.current = { userId, count: 0 };
          setAnswer({ userId, access: viewerAccessFor(true, profile), profile: profile ?? null });
          return;
        } catch (error) {
          if (cancelled) return;
          // The last attempt must answer, or the page waits forever; it answers "incomplete".
          if (attempt === PROFILE_FETCH_ATTEMPTS - 1) {
            const before = failures.current.userId === userId ? failures.current.count : 0;
            failures.current = { userId, count: before + 1 };
            setAnswer({ userId, access: viewerAccessFor(true, null, error), profile: null, failed: true });
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, authLoading, reloadKey]);

  // A failed read must not settle the whole session: PlayerProvider lives for all of it, so a
  // set-up account whose profile couldn't be read once would hear previews until a reload. Read
  // again when the page comes back into view or the network returns, and on a growing timer.
  const failedFor = answer?.failed ? answer.userId : null;
  useEffect(() => {
    if (!userId || failedFor !== userId) return;
    const reread = () => setReloadKey((key) => key + 1);
    const rereadIfVisible = () => {
      if (document.visibilityState === "visible") reread();
    };
    const inARow = Math.max(1, Math.min(failures.current.count, PROFILE_REREAD_DELAYS_MS.length));
    const delay = PROFILE_REREAD_DELAYS_MS[inARow - 1];
    const timer = setTimeout(reread, delay);
    window.addEventListener("focus", reread);
    window.addEventListener("online", reread);
    document.addEventListener("visibilitychange", rereadIfVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", reread);
      window.removeEventListener("online", reread);
      document.removeEventListener("visibilitychange", rereadIfVisible);
    };
  }, [userId, failedFor, answer]);

  if (authLoading) return { access: "loading", user, profile: null };
  if (!user) return { access: "signed-out", user: null, profile: null };
  const current = answer?.userId === user.id ? answer : cachedAnswer(user.id);
  if (!current) return { access: "loading", user, profile: null };
  return { access: current.access, user, profile: current.profile };
}
